import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';
import { allPosts } from '@/mocks/allPosts';
import { getPublishedPosts } from '@/utils/scheduler';

const POPULAR_SLUGS = [
  'red-flags-vs-green-flags-dating',
  'attachment-styles-explained',
  'five-love-languages-explained',
  'healing-after-a-breakup-timeline',
  'why-good-relationships-feel-boring',
  'setting-healthy-boundaries-relationship',
];

export default function NotFound() {
  const location = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const published = useMemo(() => getPublishedPosts(), []);

  const popularPosts = useMemo(
    () =>
      POPULAR_SLUGS.map((slug) => published.find((p) => p.slug === slug)).filter(
        Boolean
      ) as typeof allPosts,
    [published]
  );

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
    }
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-white dark:bg-stone-950 pt-20 pb-16 transition-colors duration-300">
        {/* Hero error block */}
        <section className="max-w-3xl mx-auto px-4 md:px-8 text-center py-16 md:py-20">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-rose-50 dark:bg-rose-900/20 mb-6">
            <i className="ri-map-pin-line text-3xl text-rose-400" />
          </div>
          <h1 className="text-6xl md:text-8xl font-black text-stone-100 dark:text-stone-800 select-none leading-none mb-2"
            style={{ fontFamily: "'Teko', sans-serif" }}>
            404
          </h1>
          <h2 className="text-2xl md:text-3xl font-bold text-stone-800 dark:text-stone-100 mb-3"
            style={{ fontFamily: "'Lora', serif" }}>
            This page doesn&apos;t exist
          </h2>
          <p className="text-stone-500 dark:text-stone-400 text-base mb-2 max-w-md mx-auto leading-relaxed">
            The page at <code className="text-xs bg-stone-100 dark:bg-stone-800 text-rose-500 px-2 py-0.5 rounded font-mono">{location.pathname}</code> couldn&apos;t be found.
            It may have been moved, renamed, or never existed.
          </p>
          <p className="text-stone-400 dark:text-stone-500 text-sm mb-10">
            Try searching below, or browse our most-read articles.
          </p>

          {/* Search bar */}
          <form onSubmit={handleSearch} className="flex items-center gap-2 max-w-md mx-auto">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search articles..."
                className="w-full pl-9 pr-4 py-2.5 text-sm rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 text-stone-800 dark:text-stone-100 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-rose-300 dark:focus:ring-rose-700 transition"
              />
            </div>
            <button
              type="submit"
              className="whitespace-nowrap px-5 py-2.5 bg-rose-500 hover:bg-rose-600 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer"
            >
              Search
            </button>
          </form>

          <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
            <Link
              to="/"
              className="whitespace-nowrap inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-stone-600 dark:text-stone-300 bg-stone-100 dark:bg-stone-800 hover:bg-rose-50 dark:hover:bg-rose-900/20 hover:text-rose-500 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line" /> Home
            </Link>
            <Link
              to="/archive"
              className="whitespace-nowrap inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-stone-600 dark:text-stone-300 bg-stone-100 dark:bg-stone-800 hover:bg-rose-50 dark:hover:bg-rose-900/20 hover:text-rose-500 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-article-line" /> All Articles
            </Link>
            <Link
              to="/contact"
              className="whitespace-nowrap inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-stone-600 dark:text-stone-300 bg-stone-100 dark:bg-stone-800 hover:bg-rose-50 dark:hover:bg-rose-900/20 hover:text-rose-500 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-mail-line" /> Contact
            </Link>
          </div>
        </section>

        {/* Popular articles */}
        <section className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="border-t border-stone-100 dark:border-stone-800 pt-12">
            <h3 className="text-xs font-semibold text-stone-400 dark:text-stone-500 uppercase tracking-widest mb-6 text-center">
              Most-Read Articles
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {popularPosts.map((post) => {
                const cat = categories.find((c) => c.slug === post.category);
                return (
                  <Link
                    key={post.id}
                    to={`/post/${post.slug}`}
                    className="group flex gap-4 p-4 rounded-xl border border-stone-100 dark:border-stone-800 hover:border-rose-200 dark:hover:border-rose-800 bg-white dark:bg-stone-900 hover:bg-rose-50/40 dark:hover:bg-rose-900/10 transition-all cursor-pointer"
                  >
                    <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      {cat && (
                        <span
                          className="text-xs font-semibold uppercase tracking-wider"
                          style={{ color: cat.color }}
                        >
                          {cat.name}
                        </span>
                      )}
                      <p className="text-sm font-semibold text-stone-800 dark:text-stone-100 leading-snug mt-0.5 line-clamp-2 group-hover:text-rose-600 dark:group-hover:text-rose-400 transition-colors"
                        style={{ fontFamily: "'Lora', serif" }}>
                        {post.title}
                      </p>
                      <span className="text-xs text-stone-400 dark:text-stone-500 mt-1 flex items-center gap-1">
                        <i className="ri-time-line" />{post.readTime} min read
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* Browse by category */}
        <section className="max-w-7xl mx-auto px-4 md:px-8 mt-14">
          <h3 className="text-xs font-semibold text-stone-400 dark:text-stone-500 uppercase tracking-widest mb-5 text-center">
            Browse by Topic
          </h3>
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="whitespace-nowrap inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium border transition-all cursor-pointer hover:scale-105"
                style={{
                  borderColor: `${cat.color}40`,
                  color: cat.color,
                  backgroundColor: `${cat.color}0d`,
                }}
              >
                <i className="ri-bookmark-line text-xs" />
                {cat.name}
              </Link>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
